import type { Category } from "../types";
import { isHandled, type BlockedKind } from "./blocked-items";
import {
  BLOCKED_SCRIPT_TYPE,
  isExplicitlyAllowed,
  log,
  readTaggedCategory,
  SRC_ATTR,
  type BlockingContext,
} from "./context";
import { releaseItem } from "./unblock";

/**
 * Backstop for nodes that never pass through a patched setter: parser-inserted tags and
 * anything written by `document.write`. Rewriting `type` to text/plain reliably stops
 * *execution*; it cannot always stop the *request*, because the preload scanner may have
 * started it during parsing. That residual gap is why static tracker tags should be tagged
 * in markup — the auditor flags the ones that are not.
 */
export function installMutationGuard(context: BlockingContext): MutationObserver | null {
  if (typeof MutationObserver !== "function") return null;

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of Array.from(record.addedNodes)) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        inspect(node as Element, context);
        for (const nested of Array.from((node as Element).querySelectorAll?.("script,iframe,img") ?? [])) {
          inspect(nested, context);
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  return observer;
}

/** Sweeps elements already parsed before the observer attached. */
export function sweepExisting(context: BlockingContext): void {
  for (const element of Array.from(document.querySelectorAll("script,iframe,img"))) {
    inspect(element, context);
  }
}

function inspect(element: Element, context: BlockingContext): void {
  const kind = kindOf(element);
  if (!kind || isHandled(element) || isExplicitlyAllowed(element)) return;

  const tagged = readTaggedCategory(element);
  if (tagged) {
    holdTagged(element, kind, tagged, context);
    return;
  }

  const url = element.getAttribute("src");
  if (!url) return;

  const category = context.blocklist.categoryFor(url);
  if (!category || context.isGranted(category)) return;

  if (kind === "script") {
    const scriptType = element.getAttribute("type") ?? "";
    context.items.add({ kind, source: "observer", element, url, category, scriptType });
    element.setAttribute("type", BLOCKED_SCRIPT_TYPE);
    element.removeAttribute("src");
  } else {
    context.items.add({ kind, source: "observer", element, url, category });
    element.removeAttribute("src");
  }
  log(context, `held ${kind} (${category}) via observer:`, url);
}

/**
 * Markup-tagged elements. These carry their URL in `data-ck-src` (or hold an inline body
 * behind `type="text/plain"`), so nothing was ever requested — the leak-free path.
 *
 * A granted category is *not* a reason to skip one. Tagging moves the URL out of `src`, so
 * the browser has nothing to fetch and the element is inert until something hands the URL
 * back; walking away here would silently retire the tag for the life of the page rather
 * than let it run. That is reachable in both regimes — a returning EU visitor whose stored
 * decision already grants the category, and every US visitor, since the opt-out baseline
 * grants it from the start — so the element is recorded and then released on the spot.
 */
function holdTagged(
  element: Element,
  kind: BlockedKind,
  category: Category,
  context: BlockingContext,
): void {
  const url = element.getAttribute(SRC_ATTR) ?? element.getAttribute("src") ?? "";
  const inline = kind === "script" && !url ? (element.textContent ?? "") : undefined;

  const item = context.items.add({
    kind,
    source: "tagged",
    element,
    url,
    category,
    inline,
    scriptType: element.getAttribute("data-ck-type") ?? "",
  });

  if (kind === "script") element.setAttribute("type", BLOCKED_SCRIPT_TYPE);
  element.removeAttribute("src");

  if (context.isGranted(category)) {
    releaseItem(item);
    log(context, `released tagged ${kind} (${category}) on sight:`, url || "inline");
    return;
  }
  log(context, `held tagged ${kind} (${category}):`, url || "inline");
}

function kindOf(element: Element): BlockedKind | null {
  switch (element.tagName) {
    case "SCRIPT":
      return "script";
    case "IFRAME":
      return "iframe";
    case "IMG":
      return "img";
    default:
      return null;
  }
}
